import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { formatMessageTime } from "../../utils/timeUtils";
import { TaskReferenceMedia } from "./TaskReferenceMedia";
import { B2BChatTip } from "../b2b/B2BChatTip";
import { EvidenceBadge } from "./EvidenceBadge";
import { fmtRub } from "../../utils/priceChange";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TaskChatOnly = ({ task, user, showToast }) => {
 const [messages, setMessages] = useState([]);
 const [newMessage, setNewMessage] = useState("");
 const [loading, setLoading] = useState(true);
 const [showComplaintModal, setShowComplaintModal] = useState(false);
 const [complaintText, setComplaintText] = useState("");
 const [complaintSubmitting, setComplaintSubmitting] = useState(false);
 const [complaintSent, setComplaintSent] = useState(false);
 const [activePriceChange, setActivePriceChange] = useState(null);
 const chatEndRef = useRef(null); // Add ref for scroll
 const prevMessagesLength = useRef(0); // Track message count

 const isCustomer = String(task.customer_id) === String(user.id);
 const chatOtherId = isCustomer
 ? (task.discussing_executor_id || task.executor_id)
 : task.customer_id;

 useEffect(() => {
 fetchMessages();
 fetchActivePriceChange();
 const interval = setInterval(() => {
 fetchMessages();
 fetchActivePriceChange();
 }, 3000);
 return () =>clearInterval(interval);
 }, []);

 // Активный запрос на изменение цены (Д10: блок «Согласиться/Отклонить» и в этом чате)
 const fetchActivePriceChange = async () => {
 try {
 const response = await axios.get(`${API}/tasks/${task.id}/active-price-change`);
 setActivePriceChange(response.data);
 } catch (error) {
 if (error.response?.status === 404) setActivePriceChange(null);
 }
 };

 const respondToPriceChange = async (action, reason = "") => {
 try {
 const response = await axios.post(`${API}/tasks/${task.id}/respond-price-change`, { action, reason });
 showToast(response.data.message, "success");
 fetchActivePriceChange();
 fetchMessages();
 if (action === "confirm" && response.data.new_price) task.price = response.data.new_price;
 } catch (error) {
 showToast(error.response?.data?.detail || "Не удалось отправить ответ на изменение цены. Попробуйте ещё раз.", "error");
 }
 };

 // Auto-scroll when messages change
 useEffect(() => {
 if (messages.length > 0) {
 setTimeout(() => {
 scrollToBottom();
 }, 100);
 }
 }, [messages.length]);

 // Auto-scroll when messages change
 useEffect(() => {
 if (messages.length >prevMessagesLength.current) {
 setTimeout(() => {
 scrollToBottom();
 }, 100);
 prevMessagesLength.current = messages.length;
 }
 }, [messages]);

 const scrollToBottom = () => {
 if (chatEndRef.current) {
 const chatContainer = chatEndRef.current.parentElement;
 if (chatContainer) {
 chatContainer.scrollTop = chatContainer.scrollHeight;
 }
 chatEndRef.current.scrollIntoView({ behavior: "auto", block: "end" });
 }
 };

 const fetchMessages = async () => {
 try {
 // Get API base URL
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API || !task?.id) {
 console.error('Missing API URL or task ID:', { API, taskId: task?.id });
 setLoading(false);
 return;
 }
 
 const response = await axios.get(`${API}/api/tasks/${task.id}/messages`);
 setMessages(response.data);
 } catch (error) {
 console.error("Error fetching messages:", error);
 // Don't show error toast for network issues to avoid spam
 if (error.response?.status >= 500 || !error.response) {
 console.warn("Network/server error fetching messages, will retry");
 }
 } finally {
 setLoading(false);
 }
 };

 const sendMessage = async (e) => {
 e.preventDefault();
 if (!newMessage.trim()) return;

 try {
 const resp = await axios.post(`${API}/tasks/${task.id}/messages`, {
 task_id: task.id,
 message: newMessage
 });
 if (resp.data?.warning) showToast(resp.data.warning, "warning");
 setNewMessage("");
 fetchMessages();
 // Force scroll after sending
 setTimeout(() => {
 scrollToBottom();
 }, 300);
 } catch (error) {
 showToast("Ошибка при отправке сообщения", "error");
 }
 };

 const submitComplaint = async () => {
 if (!complaintText.trim()) {
 showToast("Опишите суть жалобы", "error");
 return;
 }
 if (!chatOtherId) {
 showToast("Не удалось определить пользователя", "error");
 return;
 }
 const BACKEND = process.env.REACT_APP_BACKEND_URL;
 try {
 setComplaintSubmitting(true);
 await axios.post(`${BACKEND}/api/complaints`, {
 target_user_id: chatOtherId,
 task_id: task.id,
 description: complaintText.trim()
 });
 showToast("Жалоба подана. Администрация рассмотрит её в течение 3 дней.", "success");
 setComplaintText("");
 setShowComplaintModal(false);
 setComplaintSent(true);
 } catch (error) {
 const msg = error.response?.data?.detail || "Ошибка при подаче жалобы";
 showToast(msg, "error");
 } finally {
 setComplaintSubmitting(false);
 }
 };

 return (
 <div className="flex flex-col h-full min-h-0" data-testid="available-task-chat-root">
 {/* N-3: подсказки, медиа и сообщения — в одной прокручиваемой области; док ввода не вытесняется */}
 <div className="flex-1 min-h-0 overflow-y-auto" data-testid="available-task-chat-scroll">
 {/* Подсказка: согласовать объём работы, стоимость и критерии результата до принятия задания */}
 <div className="px-4 pt-3 pb-2 text-xs flex items-start gap-2" data-testid="discussion-chat-tip" style={{background:'#FFFBEB', borderBottom:'1px solid #FDE68A', color:'#92400E'}}>
 <span>Совет:</span>
 <span>согласуйте здесь объём работы, итоговую стоимость и критерии результата до того, как исполнитель примет задание в работу.</span>
 </div>
 {!isCustomer && <div className="px-4 pt-2"><EvidenceBadge task={task} testId="chat-evidence-badge" /></div>}
 <B2BChatTip task={task} />

 {/* Фотографии, приложенные заказчиком к заданию — видны на этапе обсуждения */}
 <TaskReferenceMedia task={task} />

 {/* Модальное окно жалобы */}
 {showComplaintModal && createPortal(
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{zIndex:10050}}>
 <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
 <h3 className="text-base font-semibold text-gray-900 mb-1">Пожаловаться на пользователя</h3>
 <p className="text-sm text-gray-500 mb-4">Жалоба будет рассмотрена администрацией в течение 3 рабочих дней.
 </p>
 <textarea
 data-testid="complaint-description-input"
 value={complaintText}
 onChange={(e) =>setComplaintText(e.target.value)}
 placeholder="Опишите, что произошло. Чем подробнее — тем быстрее рассмотрим."
 rows={4}
 className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
 />
 <div className="flex gap-2 mt-4">
 <button
 data-testid="complaint-submit-btn"
 onClick={submitComplaint}
 disabled={complaintSubmitting || !complaintText.trim()}
 className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
 >
 {complaintSubmitting ? "Отправляем..." : "Подать жалобу"}
 </button>
 <button
 onClick={() => { setShowComplaintModal(false); setComplaintText(""); }}
 className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 text-sm"
 >Отмена
 </button>
 </div>
 </div>
 </div>
 , document.body)}

 {/* Messages area */}
 <div className="p-4 space-y-3 min-h-[120px]" data-testid="available-task-chat-messages">
 {loading ? (
 <div className="text-center text-gray-500">Загрузка сообщений...</div>
 ) : messages.length === 0 ? (
 <div className="text-center text-gray-500">
 <p className="mb-2">Начните обсуждение</p>
 <p className="text-sm">Обсудите детали задания с заказчиком перед принятием</p>
 </div>
 ) : (
 messages.map((message, index) => (
 <div 
 key={index}
 className={`flex ${String(message.sender_id || message.user_id) === String(user.id) ? 'justify-end' : 'justify-start'}`}
 >
 <div className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
 String(message.sender_id || message.user_id) === String(user.id)
 ? 'bg-blue-600 text-white' 
 : 'bg-gray-200 text-gray-800'
 }`}>
 <div className="font-medium text-xs mb-1 opacity-75">
 {(() => {
 const messageId = message.sender_id || message.user_id;
 if (String(messageId) === String(user.id)) return 'Вы';
 return String(messageId) === String(task?.customer_id) ? 'Заказчик' : 'Исполнитель';
 })()}
 </div>
 <div>{message.message}</div>
 <div className="text-xs mt-1 opacity-75">
 {formatMessageTime(message.created_at)}
 </div>
 </div>
 </div>
 ))
 )}
 {/* Auto-scroll target */}
 <div ref={chatEndRef} />
 </div>
 </div>

 {/* Message input: закреплён внизу, при нехватке высоты прокручивается сам */}
 <div className="flex-shrink-0 border-t p-3 sm:p-4 bg-white overflow-y-auto" style={{maxHeight:'60%'}} data-testid="available-task-chat-dock">
 {/* Д10: изменение цены — заказчик видит ожидание, исполнитель может согласиться/отклонить */}
 {isCustomer && activePriceChange?.status === "proposed" && (
 <div className="mb-3 px-3 py-2.5 rounded-md" data-testid="price-change-waiting" style={{background:'#FFF7ED', border:'1px solid #FDBA74'}}>
 <p className="text-sm font-semibold" style={{color:'#9A3412'}}>Ожидается ответ исполнителя</p>
 <p className="text-sm" style={{color:'#9A3412'}}>Предложена новая цена: <strong>{fmtRub(activePriceChange.old_price)} ₽ → {fmtRub(activePriceChange.new_price)} ₽</strong></p>
 {activePriceChange.reason && <p className="text-sm" style={{color:'#9A3412'}}>Причина: {activePriceChange.reason}</p>}
 </div>
 )}
 {!isCustomer && activePriceChange?.status === "proposed" && (
 <div className="mb-3 px-3 py-2.5 rounded-md" data-testid="price-change-offer" style={{background:'#EFF6FF', border:'1px solid #93C5FD'}}>
 <p className="text-sm font-semibold" style={{color:'#1E3A8A'}}>Предложение изменения цены</p>
 <p className="text-sm" style={{color:'#1E40AF'}}>Заказчик предлагает: <strong>{fmtRub(activePriceChange.old_price)} ₽ → {fmtRub(activePriceChange.new_price)} ₽</strong></p>
 {activePriceChange.reason && <p className="text-sm mb-1" style={{color:'#1E40AF'}}>Причина: {activePriceChange.reason}</p>}
 <div className="flex gap-2 mt-2">
 <button
 data-testid="price-change-confirm-btn"
 onClick={() =>respondToPriceChange("confirm")}
 className="flex-1 bg-green-600 text-white py-2 px-3 rounded-md hover:bg-green-700 text-sm font-medium"
 >Согласиться
 </button>
 <button
 data-testid="price-change-reject-btn"
 onClick={() => { const reason = prompt("Причина отклонения (опционально):"); respondToPriceChange("reject", reason || ""); }}
 className="flex-1 bg-red-600 text-white py-2 px-3 rounded-md hover:bg-red-700 text-sm font-medium"
 >Отклонить
 </button>
 </div>
 </div>
 )}
 <form onSubmit={sendMessage} className="flex space-x-2">
 <input
 type="text"
 data-testid="available-task-chat-input"
 value={newMessage}
 onChange={(e) =>setNewMessage(e.target.value)}
 placeholder="Напишите сообщение заказчику..."
 className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
 />
 <button
 type="submit"
 data-testid="available-task-chat-send-btn"
 disabled={!newMessage.trim()}
 className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
 >
 Отправить
 </button>
 </form>
 {/* Кнопка "Пожаловаться" */}
 {chatOtherId && (
 <div className="mt-2 text-right">
 {complaintSent ? (
 <span className="text-xs text-gray-400">Жалоба подана</span>
 ) : (
 <button
 data-testid="report-user-btn"
 onClick={() =>setShowComplaintModal(true)}
 className="text-xs text-red-500 hover:text-red-700 underline"
 >Пожаловаться на пользователя
 </button>
 )}
 </div>
 )}
 </div>
 </div>
 );
};
